package com.karyadina.ui.screens

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowForward
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.School
import androidx.compose.material.icons.filled.Code
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Science
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.UserPersona
import com.karyadina.ui.theme.StudyTheme
import com.karyadina.ui.theme.StudyStrings
import com.karyadina.ui.getPersonaName
import com.karyadina.ui.getPersonaDescription
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalAnimationApi::class)
@Composable
fun OnboardingScreen(
    onPersonaSelected: (UserPersona) -> Unit
) {
    var selectedPersona by remember { mutableStateOf<UserPersona?>(null) }
    
    Scaffold(
        containerColor = MaterialTheme.colorScheme.surface,
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            Column(
                modifier = Modifier.fillMaxSize()
            ) {
                // Header
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        stringResource(StudyStrings.OnboardingTitle),
                        style = MaterialTheme.typography.displaySmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    Spacer(Modifier.height(8.dp))
                    Text(
                        stringResource(StudyStrings.OnboardingSubtitle),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                    )
                }

                // Path List
                Box(modifier = Modifier.weight(1f)) {
                    
                    // Draw the dashed line in the background
                    Canvas(modifier = Modifier.fillMaxSize()) {
                        val lineX = 40.dp.toPx() // Align with the icons
                        drawLine(
                            color = Color.LightGray.copy(alpha=0.5f),
                            start = Offset(lineX, 0f),
                            end = Offset(lineX, size.height),
                            strokeWidth = 2.dp.toPx(),
                            pathEffect = PathEffect.dashPathEffect(floatArrayOf(20f, 20f))
                        )
                    }

                    LazyColumn(
                        contentPadding = PaddingValues(bottom = 80.dp),
                        modifier = Modifier.fillMaxSize()
                    ) {
                        itemsIndexed(UserPersona.entries) { index, persona ->
                            PersonaPathItem(
                                persona = persona,
                                isSelected = selectedPersona == persona,
                                onClick = { selectedPersona = persona }
                            )
                        }
                    }
                }
            }

            // Floating Action Button or Bottom Bar for 'Continue'
            AnimatedVisibility(
                visible = selectedPersona != null,
                enter = slideInVertically { it } + fadeIn(),
                exit = slideOutVertically { it } + fadeOut(),
                modifier = Modifier
                    .align(Alignment.BottomCenter)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp)
                        .background(Color.Transparent)
                ) {
                    Button(
                        onClick = { selectedPersona?.let { onPersonaSelected(it) } },
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.primary
                        ),
                        elevation = ButtonDefaults.buttonElevation(defaultElevation = 8.dp)
                    ) {
                        Text(stringResource(StudyStrings.OnboardingContinue), style = MaterialTheme.typography.titleMedium)
                        Spacer(Modifier.width(8.dp))
                        Icon(Icons.Default.ArrowForward, contentDescription = null)
                    }
                }
            }
        }
    }
}

@Composable
fun PersonaPathItem(
    persona: UserPersona,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val themeColor = Color(persona.themeColor)
    
    val transition = updateTransition(isSelected, label = "Selection")
    val scale by transition.animateFloat(label = "Scale") { if (it) 1.05f else 1.0f }
    val cardColor by transition.animateColor(label = "CardColor") { 
        if (it) themeColor.copy(alpha = 0.1f) // Subtle tint of theme color
        else MaterialTheme.colorScheme.surfaceContainerHigh 
    }
    
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Timeline Dot / Icon
        Box(
            modifier = Modifier.width(48.dp),
            contentAlignment = Alignment.Center
        ) {
            Surface(
                shape = CircleShape,
                color = if (isSelected) themeColor else MaterialTheme.colorScheme.surfaceVariant,
                modifier = Modifier.size(if (isSelected) 40.dp else 24.dp).animateContentSize()
            ) {
                Box(contentAlignment = Alignment.Center) {
                    if (isSelected) {
                        Icon(
                            imageVector = getPersonaIcon(persona),
                            contentDescription = null,
                            tint = Color.White, // Always white on colored background
                            modifier = Modifier.size(20.dp)
                        )
                    } else {
                        // Small dot
                         Box(modifier = Modifier.size(8.dp).background(MaterialTheme.colorScheme.onSurfaceVariant, CircleShape))
                    }
                }
            }
        }
        
        Spacer(modifier = Modifier.width(16.dp))
        
        // Card Content
        Card(
            modifier = Modifier
                .weight(1f)
                .alpha(if (isSelected) 1f else 0.8f),
            shape = MaterialTheme.shapes.medium,
            colors = CardDefaults.cardColors(containerColor = cardColor),
            border = if (isSelected) BorderStroke(2.dp, themeColor) else null
        ) {
            Column(
                modifier = Modifier.padding(16.dp)
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = getPersonaName(persona),
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    if (isSelected) {
                        Icon(Icons.Default.Check, null, tint = themeColor)
                    }
                }
                
                Spacer(modifier = Modifier.height(4.dp))
                
                Text(
                    text = getPersonaDescription(persona),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

fun getPersonaIcon(persona: UserPersona): ImageVector {
    return when (persona) {
        UserPersona.STUDENT -> Icons.Default.School
        UserPersona.PROFESSIONAL -> Icons.Default.Person // Briefcase not in default extended set usually? Using Person
        UserPersona.DEVELOPER -> Icons.Default.Code
        UserPersona.CONTENT_CREATOR -> Icons.Default.Edit
        UserPersona.RESEARCHER -> Icons.Default.Science
        UserPersona.GENERAL -> Icons.Default.Person
    }
}
